import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Car, Mail, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientAuth = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/client/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al iniciar sesión');
      }

      const data = await response.json();
      
      // Store token and user data
      localStorage.setItem('session_token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      toast.success('¡Bienvenido!');
      
      // If first login, redirect to change password
      if (data.first_login) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12" style={{
      background: 'linear-gradient(135deg, #e8f9ed 0%, #d4f1e0 50%, #c8eed8 100%)'
    }}>
      <div className="w-full max-w-md mx-auto px-6">
        {/* Logo and Header */}
        <div className="text-center mb-8 mt-8">
          <div className="inline-flex items-center justify-center gap-4 mb-6">
            <div className="bg-[#0a3042] rounded-2xl p-3 shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_927b78ff-6137-4209-a5c6-1343491e5188/artifacts/04fvidd7_1c.png" 
                alt="AutoGo Icon" 
                className="h-14 w-14"
              />
            </div>
            <span className="text-5xl font-bold tracking-wide" style={{color: '#0a3042'}}>AutoGo</span>
          </div>
          
          <h1 className="text-3xl font-bold mb-3" style={{color: '#0a3042'}}>
            {t('welcome_back')}
          </h1>
          <p className="text-gray-600">
            {t('sign_in_to_manage')}
          </p>
        </div>

        {/* Auth Card */}
        <div className="glass-card p-8 rounded-2xl" style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email Field */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                {t('email_address')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('email_placeholder')}
                  required
                  className="pl-10 py-6 text-base"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
                {t('password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 pr-10 py-6 text-base"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm font-medium hover:underline"
                style={{color: '#36e961'}}
              >
                {t('forgot_password')}
              </button>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full py-6 text-base font-semibold text-white"
              style={{backgroundColor: '#36e961'}}
            >
              {loading ? t('loading') : t('sign_in')}
            </Button>
          </form>

          {/* Additional Info */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-600 mb-2 font-medium">
              {t('no_account')}
            </p>
            <div className="flex flex-col items-center gap-1">
              <a 
                href="mailto:admin@autogodelivery.com" 
                className="text-xs font-semibold hover:underline"
                style={{color: '#36e961'}}
              >
                📧 admin@autogodelivery.com
              </a>
              <a 
                href="tel:+34930491501" 
                className="text-xs font-semibold hover:underline"
                style={{color: '#36e961'}}
              >
                📞 +34 930 49 15 01
              </a>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('back_to_home')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientAuth;